import { Component, ViewChild, ElementRef, AfterViewInit, DoCheck, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ConsoleErrorService } from './console-error.service';
import { userData } from 'src/app/STORE/selectors/auth.selector';

@Component({
  selector: 'app-console-error-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './console-error-panel.component.html',
  styleUrls: ['./console-error-panel.component.scss']
})
export class ConsoleErrorPanel implements AfterViewInit, DoCheck {
  open = false;
  fullscreen = false;
  position: 'left' | 'right' = 'right';
  @ViewChild('errorList', { static: false }) errorListRef?: ElementRef;
  private lastErrorCount = 0;
  get errors() {
    return this.consoleErrorService?.getErrors() || [];
  }

  constructor(
    private consoleErrorService: ConsoleErrorService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private store: Store
  ) {}

  ngAfterViewInit() {
    this.lastErrorCount = this.errors?.length || 0;
  }

  ngDoCheck() {
    /* 
      Temporily commented out

    // If new error(s) added while collapsed, expand and scroll to bottom
    if ((this.errors?.length || 0) > this.lastErrorCount) {
      this.open = true;
      this.cdr?.detectChanges();
      setTimeout(() => {
        if (this.errorListRef?.nativeElement) {
          this.errorListRef.nativeElement.scrollTop = this.errorListRef.nativeElement.scrollHeight;
        }
      }, 0);
    }
    this.lastErrorCount = this.errors?.length || 0; */
  }

  togglePanel() {
    this.open = !this.open;
  }

  togglePosition(event: Event) {
    event.stopPropagation();
    this.position = this.position === 'left' ? 'right' : 'left';
  }

  toggleFullscreen(event: Event) {
    event.stopPropagation();
    this.fullscreen = true;
  }

  closeFullscreen() {
    this.fullscreen = false;
  }

  private getBrowserInfo(): string {
    const userAgent = navigator?.userAgent || '';
    let browserName = 'Unknown Browser';
    let browserVersion = 'Unknown Version';

    try {
      // Chrome
      if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
        browserName = 'Chrome';
        const chromeMatch = userAgent.match(/Chrome\/([0-9.]+)/);
        if (chromeMatch) browserVersion = chromeMatch[1];
      }
      // Edge (Chromium-based)
      else if (userAgent.includes('Edg')) {
        browserName = 'Microsoft Edge';
        const edgeMatch = userAgent.match(/Edg\/([0-9.]+)/);
        if (edgeMatch) browserVersion = edgeMatch[1];
      }
      // Firefox
      else if (userAgent.includes('Firefox')) {
        browserName = 'Firefox';
        const firefoxMatch = userAgent.match(/Firefox\/([0-9.]+)/);
        if (firefoxMatch) browserVersion = firefoxMatch[1];
      }
      // Safari
      else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
        browserName = 'Safari';
        const safariMatch = userAgent.match(/Version\/([0-9.]+)/);
        if (safariMatch) browserVersion = safariMatch[1];
      }
      // Internet Explorer
      else if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
        browserName = 'Internet Explorer';
        const ieMatch = userAgent.match(/(?:MSIE |rv:)([0-9.]+)/);
        if (ieMatch) browserVersion = ieMatch[1];
      }
      // Opera
      else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
        browserName = 'Opera';
        const operaMatch = userAgent.match(/(?:Opera|OPR)\/([0-9.]+)/);
        if (operaMatch) browserVersion = operaMatch[1];
      }
    } catch (e) {
      console.warn('Error detecting browser info:', e);
    }

    return `${browserName} ${browserVersion}`;
  }

  async copyError(error: any, event: Event) {
    event.stopPropagation();

    try {
      // Get current user info
      let currentUser = 'Unknown User';
      this.store
        .select(userData)
        .subscribe((user: any) => {
          if (user?.email) {
            currentUser = `${user.firstName || ''} ${user.lastName || ''} (${user.email})`.trim();
          } else if (user?.firstName || user?.lastName) {
            currentUser = `${user.firstName || ''} ${user.lastName || ''}`.trim();
          }
        })
        .unsubscribe();

      // Get current URL
      const currentUrl = window?.location?.href || 'Unknown URL';

      // Get current timestamp
      const timestamp = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });

      // Get browser information
      const browserInfo = this.getBrowserInfo();

      // Get OS information
      const osInfo = navigator?.platform || 'Unknown OS';

      // Format the error information
      const errorInfo = `
=== ERROR REPORT ===
Captured Time: ${timestamp}
User: ${currentUser}
URL: ${currentUrl}
Browser: ${browserInfo}
Operating System: ${osInfo}
User Agent: ${navigator?.userAgent || 'Unknown User Agent'}

Error Message: ${error?.message || 'Unknown Error'}

Stack Trace:
${error?.stack || 'No stack trace available'}

=== END OF REPORT ===
      `.trim();

      // Copy to clipboard
      await navigator?.clipboard?.writeText(errorInfo);

      // Optional: Show a brief success indication (you can customize this)
      console.log('Error details copied to clipboard');
    } catch (err) {
      console.error('Failed to copy error details:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = `Error: ${error?.message || 'Unknown Error'}\nStack: ${error?.stack || 'No stack trace'}\nURL: ${window?.location?.href || 'Unknown URL'}`;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }

  async copyAllErrors(event: Event) {
    event.stopPropagation();

    if ((this.errors?.length || 0) <= 1) {
      console.log('Copy All requires multiple errors');
      return;
    }

    try {
      // Get current user info
      let currentUser = 'Unknown User';
      this.store
        .select(userData)
        .subscribe((user: any) => {
          if (user?.email) {
            currentUser = `${user.firstName || ''} ${user.lastName || ''} (${user.email})`.trim();
          } else if (user?.firstName || user?.lastName) {
            currentUser = `${user.firstName || ''} ${user.lastName || ''}`.trim();
          }
        })
        .unsubscribe();

      // Get current URL
      const currentUrl = window?.location?.href || 'Unknown URL';

      // Get current timestamp
      const timestamp = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });

      // Get browser information
      const browserInfo = this.getBrowserInfo();

      // Get OS information
      const osInfo = navigator.platform || 'Unknown OS';

      // Format all errors
      const errorSections =
        this.errors
          ?.map((error, index) => {
            return `
--- ERROR ${index + 1} ---
Message: ${error?.message || 'Unknown Error'}
Stack Trace:
${error?.stack || 'No stack trace available'}
`;
          })
          .join('\n') || 'No errors available';

      // Format the complete error report
      const allErrorsInfo = `
=== BULK ERROR REPORT ===
Timestamp: ${timestamp}
User: ${currentUser}
URL: ${currentUrl}
Browser: ${browserInfo}
Operating System: ${osInfo}
User Agent: ${navigator?.userAgent || 'Unknown User Agent'}
Total Errors: ${this.errors?.length || 0}

${errorSections}
=== END OF BULK REPORT ===
      `.trim();

      // Copy to clipboard
      await navigator?.clipboard?.writeText(allErrorsInfo);

      // Optional: Show a brief success indication
      console.log(`${this.errors?.length || 0} errors copied to clipboard`);
    } catch (err) {
      console.error('Failed to copy all errors:', err);
      // Fallback for older browsers
      const fallbackText =
        this.errors
          ?.map(
            (error, index) =>
              `Error ${index + 1}: ${error?.message || 'Unknown Error'}\nStack: ${error?.stack || 'No stack trace'}\n`
          )
          .join('\n') || 'No errors available' + `\nURL: ${window?.location?.href || 'Unknown URL'}`;

      const textArea = document.createElement('textarea');
      textArea.value = fallbackText;
      document?.body?.appendChild(textArea);
      textArea?.select();
      document.execCommand('copy');
      document?.body?.removeChild(textArea);
    }
  }

  toggleExpand(error: any, event: Event) {
    event?.stopPropagation();
    this.consoleErrorService?.toggleExpand(error?.id);
  }

  remove(id: number, event: Event) {
    event?.stopPropagation();
    this.consoleErrorService?.removeError(id);

    // If in fullscreen mode and all errors are removed, automatically minimize
    setTimeout(() => {
      if (this.fullscreen && (this.errors?.length || 0) === 0) {
        this.fullscreen = false;
      }
    }, 0);
  }

  clearAll(event: Event) {
    event?.stopPropagation();
    this.consoleErrorService?.clearAll();

    // If in fullscreen mode and all errors are cleared, automatically minimize
    if (this.fullscreen && (this.errors?.length || 0) === 0) {
      this.fullscreen = false;
    }
  }
}
